import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown } from '@fortawesome/free-solid-svg-icons'

/**
 * @extends {React.Component<Accordion.propTypes>} 
 */
class Accordion extends Component {
    constructor(props) {
        super(props)

        this.accordionContentRef = React.createRef()
        this.state = {
            isOpen: props.isOpen || false,
            height: 0,
            maxHeight: props.maxHeight || 300
        }
    }

    componentDidMount() {
        this.resizeObserver = new ResizeObserver(() => {
            if (this.accordionContentRef.current && this.state.isOpen) {
                this.setState({
                    height: this.accordionContentRef.current?.getBoundingClientRect()?.height
                })
            }
        });

        this.resizeObserver.observe(this.accordionContentRef.current);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.isOpen !== this.state.isOpen) {
            if (this.accordionContentRef.current) {
                this.setState({
                    height: this.accordionContentRef.current?.getBoundingClientRect()?.height
                })
            }
        }

        if (prevProps.isOpen !== this.props.isOpen) {
            this.setState({
                isOpen: this.props.isOpen
            })
        }
    }

    componentWillUnmount() {
        this.resizeObserver.disconnect()
    }

    toggleOpen = () => {
        this.setState({
            isOpen: !this.state.isOpen
        })
    }

    getAccordionStyle() {
        let height = '0px'

        if (this.state.isOpen) {
            height = `${this.state.height}px`
        }
        
        return {
            maxHeight: height,
        }
    }

    render() {
        return (
            <div className={`accordion w-full bg-white ${this.props.className || ''}`}>
                <div 
                    className={`flex justify-between items-center cursor-pointer select-none hover:bg-nexcarewhite1 text-base p-2.5 ${this.props.headerClassName || ''}`} 
                    onClick={this.props.group ? () => this.props.group.toggleOpen(this.props.group.index) : this.toggleOpen}
                >
                    <span className='text-gray-800'>{this.props.title}</span>
                    <FontAwesomeIcon className={`${this.state.isOpen ? 'transform rotate-180' : ''}`} icon={faAngleDown} size='lg' />
                </div>
                <div 
                    className={`content overflow-hidden transition-all duration-200`} 
                    style={this.getAccordionStyle()}
                >
                    <div 
                        className='overflow-auto' 
                        style={{ maxHeight: `${this.state.maxHeight}px` }}
                        ref={this.accordionContentRef}
                    >
                        {this.props.children}
                    </div>
                </div>
            </div>
        )
    }
}

Accordion.propTypes = {
    /**
     * @type {string}
     */
    title: PropTypes.string,

    /**
     * @type {string}
     */
    className: PropTypes.string,

    /**
     * @type {string}
     */
    headerClassName: PropTypes.string,

    /**
     * @type {number}
     */
    maxHeight: PropTypes.number,

    /**
     * @type {boolean}
     */
    isOpen: PropTypes.bool
}

/**
 * @extends {React.Component<AccordionGroup.propTypes>}
 */
class AccordionGroup extends Component {
    constructor(props) {
        super(props)
        
        this.state = {
            /**
             * @type {Array<{ element: Accordion, isOpen: boolean }>}
             */
            accordions: []
        }
    }

    componentDidMount() {
        this.initAccordions()
    }

    initAccordions = () => {
        const accordions = []

        React.Children.map(this.props.children, (child) => {
            if (React.isValidElement(child)) {
                accordions.push({
                    element: child,
                    isOpen: false
                })
            }
        })

        this.setState({
            accordions
        })
    }

    toggleOpen = (index) => {
        const accordions = [...this.state.accordions]

        for (let i = 0; i < accordions.length; i++) {
            if (index === i) {
                continue
            }

            accordions[i].isOpen = false
        }

        accordions[index].isOpen = !accordions[index].isOpen

        this.setState({ accordions })
    }

    getChildren() {
        return this.state.accordions.map((child, index) => {
            return React.cloneElement(child.element, {
                group: {
                    instance: this,
                    toggleOpen: this.toggleOpen,
                    index          
                },
                isOpen: child.isOpen,
                key: index
            })
        })
    }

    render() {
        return this.getChildren()
    }
}

AccordionGroup.propTypes = {
    /**
     * @type {string}
     */
    children: (props, propName, componentName) => {
        const prop = props[propName]

        let error = null
        
        React.Children.forEach(prop, (child) => {
            if (child.type !== Accordion) {
                error = new Error(`${componentName} children should be of type "Accordion"`)
                return 
            }
        })

        return error
    },
}

Accordion.Group = AccordionGroup

export default Accordion